import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { markUnloadedResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postMarkUnloaded = async (poNumber: string) => {
	const url = apiRoutes.POSTMARKUNLOADED.replace(":poNumber", poNumber);
	const resp = await postAndValidate(url, null, markUnloadedResponseSchema);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkUnloaded = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (poNumber: string) => postMarkUnloaded(poNumber),
		onSuccess: () => {
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentUnmarkedLoaded);
			// Return the invalidation promise so the mutation stays pending (and
			// the loading spinner stays visible) until the shipment query has
			// refetched with the updated state.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to mark shipment as unloaded. Please try again.",
			);
		},
	});

	return {
		markUnloaded: mutate,
		markUnloadedAsync: mutateAsync,
		isSavingMarkUnloaded: isPending,
		error,
		data,
		resetError: reset,
	};
};
